import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useParams } from 'react-router-dom'
import { Button, Input, Modal, Badge, DarkModeToggleCompact, MultiImageUploader } from '../../components/ui'
import { Sidebar } from '../../components/layout/Sidebar'
import { 
    Sparkles, Mail, Copy, Send, DollarSign, Briefcase, RefreshCw, 
    Handshake, Plus, Trash2, Edit2, ExternalLink, Globe, 
    Eye, Tv, Info, Settings, LayoutGrid, Check, ToggleLeft, ToggleRight 
} from 'lucide-react'
import { askGemini } from '../../lib/gemini'
import { useTranslation } from 'react-i18next'
import { useTournaments } from '../../hooks/useTournaments'
import { useSponsors } from '../../hooks/useSponsors'

// Presets for demo sponsors
const PRESET_SPONSORS = [
    { name: 'Rabobank', logo_url: 'https://images.unsplash.com/photo-1541872703-74c5e44368f9?auto=format&fit=crop&w=120&q=80', website_url: 'https://www.rabobank.nl', display_duration: 8 },
    { name: 'Coca-Cola', logo_url: 'https://images.unsplash.com/photo-1622483767028-3f66f32aef97?auto=format&fit=crop&w=120&q=80', website_url: 'https://www.cocacola.nl', display_duration: 5 },
    { name: 'Heineken', logo_url: 'https://images.unsplash.com/photo-1608270586620-248524c67de9?auto=format&fit=crop&w=120&q=80', website_url: 'https://www.heineken.com', display_duration: 6 },
    { name: 'Albert Heijn', logo_url: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&w=120&q=80', website_url: 'https://www.ah.nl', display_duration: 7 },
    { name: 'Red Bull', logo_url: 'https://images.unsplash.com/photo-1622543929967-230467336300?auto=format&fit=crop&w=120&q=80', website_url: 'https://www.redbull.com', display_duration: 5 }
]

export const SponsorPage = () => {
    const { t } = useTranslation()
    const { tournamentId } = useParams()
    const [activeTab, setActiveTab] = useState('manager') // 'manager' or 'pitcher'
    const { tournaments, loading: loadingTournaments } = useTournaments()
    const [selectedTournamentId, setSelectedTournamentId] = useState(tournamentId || '')
    
    // Hook for sponsors of selected tournament
    const { sponsors, loading: loadingSponsors, createSponsor, updateSponsor, deleteSponsor } = useSponsors(selectedTournamentId)

    // Form states for Sponsor CRUD
    const [showSponsorModal, setShowSponsorModal] = useState(false)
    const [editingSponsor, setEditingSponsor] = useState(null)
    const [sponsorForm, setSponsorForm] = useState({
        name: '',
        logo_url: '',
        website_url: '',
        display_duration: 5,
        is_active: true
    })

    // AI Pitch state
    const [pitchLoading, setPitchLoading] = useState(false)
    const [pitchResult, setPitchResult] = useState('')
    const [pitchForm, setPitchForm] = useState({
        target: 'local', // local, corporate, parent
        type: 'shirt', // shirt, board, ball, general
        tone: 'friendly', // friendly, formal, enthusiastic
        verenigingNaam: 'onze vereniging',
        contactNaam: ''
    })

    // Auto-select tournament when loaded
    useEffect(() => {
        if (tournamentId) {
            setSelectedTournamentId(tournamentId)
        } else if (tournaments && tournaments.length > 0 && !selectedTournamentId) {
            setSelectedTournamentId(tournaments[0].id)
        }
    }, [tournaments, selectedTournamentId, tournamentId])

    // Handle add/edit sponsor submit
    const handleSponsorSubmit = async (e) => {
        e.preventDefault()
        if (!selectedTournamentId) return alert('Selecteer eerst een toernooi!')

        try {
            if (editingSponsor) {
                await updateSponsor(editingSponsor.id, sponsorForm)
            } else {
                await createSponsor(sponsorForm)
            }
            setShowSponsorModal(false)
            setEditingSponsor(null)
            setSponsorForm({
                name: '',
                logo_url: '',
                website_url: '',
                display_duration: 5,
                is_active: true
            })
        } catch (err) {
            alert('Fout bij opslaan sponsor: ' + err.message)
        }
    }

    // Load sponsor data into form for editing
    const handleEditClick = (sponsor) => {
        setEditingSponsor(sponsor)
        setSponsorForm({
            name: sponsor.name,
            logo_url: sponsor.logo_url || '',
            website_url: sponsor.website_url || '',
            display_duration: sponsor.display_duration || 5,
            is_active: sponsor.is_active !== false
        })
        setShowSponsorModal(true)
    }

    // Apply sponsor preset
    const applyPreset = (preset) => {
        setSponsorForm({
            name: preset.name,
            logo_url: preset.logo_url,
            website_url: preset.website_url,
            display_duration: preset.display_duration,
            is_active: true
        })
    }

    // AI Generator action
    const generatePitch = async () => {
        setPitchLoading(true)
        try {
            const prompt = t('sponsor_page.prompt', {
                club: pitchForm.verenigingNaam,
                target: pitchForm.target === 'local' ? t('sponsor_page.target_local') : pitchForm.target === 'corporate' ? t('sponsor_page.target_corporate') : t('sponsor_page.target_parent'),
                type: pitchForm.type === 'shirt' ? t('sponsor_page.type_shirt') : pitchForm.type === 'board' ? t('sponsor_page.type_board') : pitchForm.type === 'ball' ? t('sponsor_page.type_ball') : t('sponsor_page.type_general'),
                tone: pitchForm.tone === 'friendly' ? t('sponsor_page.tone_friendly') : pitchForm.tone === 'formal' ? t('sponsor_page.tone_formal') : t('sponsor_page.tone_passionate'),
                contact: pitchForm.contactNaam || '[Naam]'
            });

            const text = await askGemini(prompt);
            setPitchResult(text);
        } catch (err) {
            setPitchResult(t('sponsor_page.error_generating') + err.message);
        } finally {
            setPitchLoading(false);
        }
    }

    const copyToClipboard = () => {
        navigator.clipboard.writeText(pitchResult)
        alert(t('sponsor_page.copy_success'))
    }

    const activeTournamentName = tournaments.find(t => t.id === selectedTournamentId)?.name || '...'

    return (
        <div className="p-8">
            <div className="max-w-6xl mx-auto space-y-8">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-6 animate-slideUp">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-600 shadow-lg shadow-emerald-500/20">
                                <Handshake className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className="text-4xl font-bold text-gray-900 dark:text-white leading-tight">
                                    Sponsor & Advertentie Center
                                </h1>
                                <p className="text-lg text-gray-600 dark:text-slate-400 mt-1">
                                    Monetiseer je toernooi met premium roterende advertenties op het TV-scherm en mobiele live pagina's.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <DarkModeToggleCompact />
                        </div>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex border-b border-gray-200 dark:border-white/5 pb-2">
                        <div className="flex gap-4 p-1 bg-white/50 dark:bg-white/5 rounded-2xl border border-gray-200 dark:border-white/5 shadow-inner">
                            <button
                                onClick={() => setActiveTab('manager')}
                                className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
                                    activeTab === 'manager'
                                        ? 'bg-emerald-500 text-white shadow-md'
                                        : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                <LayoutGrid size={18} />
                                Live Sponsors & Ads
                            </button>
                            <button
                                onClick={() => setActiveTab('pitcher')}
                                className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
                                    activeTab === 'pitcher'
                                        ? 'bg-emerald-500 text-white shadow-md'
                                        : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                <Sparkles size={18} />
                                AI Sponsor Pitch Generator 🤖
                            </button>
                        </div>
                    </div>

                    {/* Tab 1: Live Sponsor Manager */}
                    {activeTab === 'manager' && (
                        <div className="space-y-6 animate-fadeIn">
                            {/* Control Bar */}
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 bg-white dark:bg-[#130f22] border border-gray-100 dark:border-white/5 rounded-3xl shadow-sm">
                                <div className="flex flex-1 items-center gap-4">
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400 block mb-1">Selecteer Toernooi</label>
                                        {loadingTournaments ? (
                                            <div className="h-10 w-48 bg-gray-100 dark:bg-white/5 animate-pulse rounded-xl" />
                                        ) : (
                                            <select
                                                value={selectedTournamentId}
                                                onChange={(e) => setSelectedTournamentId(e.target.value)}
                                                className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#161224] text-gray-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-emerald-500 font-bold"
                                            >
                                                {tournaments.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>

                                    {selectedTournamentId && (
                                        <div className="hidden lg:flex items-center gap-2 mt-5 p-2 px-3 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20 rounded-xl text-xs">
                                            <Tv size={14} />
                                            <span>Sponsoren roteren automatisch onderin op het TV scherm!</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex gap-3">
                                    {selectedTournamentId && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => window.open(`/live/${selectedTournamentId}/tv`, '_blank')}
                                            icon={<Eye size={18} />}
                                        >
                                            Bekijk TV-Scherm 📺
                                        </Button>
                                    )}
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            setEditingSponsor(null)
                                            setSponsorForm({
                                                name: '',
                                                logo_url: '',
                                                website_url: '',
                                                display_duration: 5,
                                                is_active: true
                                            })
                                            setShowSponsorModal(true)
                                        }}
                                        icon={<Plus size={18} />}
                                    >
                                        Sponsor Toevoegen
                                    </Button>
                                </div>
                            </div>

                            {/* Sponsors Grid / List */}
                            {loadingSponsors ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="h-48 bg-white dark:bg-[#130f22] animate-pulse rounded-3xl border border-gray-100 dark:border-white/5" />
                                    ))}
                                </div>
                            ) : sponsors.length === 0 ? (
                                <div className="text-center py-20 bg-white dark:bg-[#130f22] border border-gray-100 dark:border-white/5 rounded-3xl">
                                    <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                                        <Handshake className="text-gray-400 dark:text-slate-500" size={40} />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Nog geen sponsoren toegevoegd</h3>
                                    <p className="text-gray-500 dark:text-slate-400 max-w-md mx-auto mb-6">
                                        Voeg sponsoren toe om logo's en weblinks te laten roteren op de TV Mode en publieke resultatenpagina's van **{activeTournamentName}**.
                                    </p>
                                    <Button
                                        variant="primary"
                                        onClick={() => setShowSponsorModal(true)}
                                        icon={<Plus size={18} />}
                                    >
                                        Eerste Sponsor Toevoegen
                                    </Button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {sponsors.map(sponsor => (
                                        <div 
                                            key={sponsor.id}
                                            className={`glass-strong premium-card rounded-3xl p-6 border transition-all duration-300 relative overflow-hidden flex flex-col justify-between ${
                                                sponsor.is_active !== false 
                                                    ? 'bg-white dark:bg-[#130f22] border-gray-100 dark:border-white/5 hover:border-emerald-500/30' 
                                                    : 'bg-white/50 dark:bg-[#130f22]/30 border-gray-100 dark:border-white/5 opacity-60'
                                            }`}
                                        >
                                            {/* Top Row: Logo & Status */}
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="h-16 w-32 bg-gray-50 dark:bg-slate-950/40 rounded-2xl border border-gray-100 dark:border-white/5 p-2.5 flex items-center justify-center overflow-hidden">
                                                    {sponsor.logo_url ? (
                                                        <img 
                                                            src={sponsor.logo_url} 
                                                            alt={sponsor.name}
                                                            className="max-h-full max-w-full object-contain"
                                                            onError={(e) => {
                                                                e.target.onerror = null
                                                                e.target.src = 'https://images.unsplash.com/photo-1541872703-74c5e44368f9?auto=format&fit=crop&w=120&q=80'
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-xs uppercase shadow">
                                                            {sponsor.name.slice(0, 3)}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => updateSponsor(sponsor.id, { is_active: sponsor.is_active === false })}
                                                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-all"
                                                        title={sponsor.is_active !== false ? 'Deactiveren' : 'Activeren'}
                                                    >
                                                        {sponsor.is_active !== false ? (
                                                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 font-bold text-[10px] uppercase border border-emerald-100 dark:border-emerald-500/20">
                                                                Actief
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-500 font-bold text-[10px] uppercase border border-gray-200 dark:border-white/5">
                                                                Gedempt
                                                            </div>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Mid Row: Info */}
                                            <div className="mb-6 space-y-2">
                                                <h4 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                                                    {sponsor.name}
                                                </h4>
                                                
                                                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-slate-400">
                                                    <div className="flex items-center gap-1">
                                                        <Tv size={13} />
                                                        <span>{sponsor.display_duration || 5}s looptijd</span>
                                                    </div>

                                                    {sponsor.website_url && (
                                                        <a 
                                                            href={sponsor.website_url} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-1 text-emerald-600 hover:text-emerald-500 transition-colors"
                                                        >
                                                            <Globe size={13} />
                                                            <span>Website</span>
                                                            <ExternalLink size={10} />
                                                        </a>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Bottom Row: Actions */}
                                            <div className="flex justify-end gap-2 border-t border-gray-100 dark:border-white/5 pt-4">
                                                <button
                                                    onClick={() => handleEditClick(sponsor)}
                                                    className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl transition-all"
                                                    title="Sponsor bewerken"
                                                >
                                                    <Edit2 size={18} />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (confirm(`Weet je zeker dat je sponsor "${sponsor.name}" wilt verwijderen?`)) {
                                                            deleteSponsor(sponsor.id)
                                                        }
                                                    }}
                                                    className="p-2 hover:bg-red-50 dark:hover:bg-red-950/20 text-gray-400 hover:text-red-500 rounded-xl transition-all"
                                                    title="Sponsor verwijderen"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab 2: AI Sponsor Pitcher (Original Content) */}
                    {activeTab === 'pitcher' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fadeIn">
                            {/* Input Section */}
                            <div className="bg-white dark:bg-[#130f22] p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-white/5">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                                    <Briefcase className="text-emerald-500" />
                                    Pitch Details
                                </h2>

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">{t('sponsor_page.target_label')}</label>
                                        <div className="grid grid-cols-3 gap-3">
                                            {[
                                                { id: 'local', label: t('sponsor_page.target_local'), icon: '🏪' },
                                                { id: 'corporate', label: t('sponsor_page.target_corporate'), icon: '🏢' },
                                                { id: 'parent', label: t('sponsor_page.target_parent'), icon: '👨‍👩‍👧' }
                                            ].map(opt => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    onClick={() => setPitchForm({ ...pitchForm, target: opt.id })}
                                                    className={`p-3 rounded-xl border transition-all text-xs font-bold flex flex-col items-center gap-1.5 ${pitchForm.target === opt.id
                                                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 shadow-sm'
                                                        : 'border-gray-200 dark:border-white/5 hover:border-gray-300 text-gray-600 dark:text-slate-400'
                                                        }`}
                                                >
                                                    <span className="text-xl">{opt.icon}</span>
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">{t('sponsor_page.type_label')}</label>
                                        <select
                                            className="w-full p-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#161224] text-gray-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-emerald-500"
                                            value={pitchForm.type}
                                            onChange={(e) => setPitchForm({ ...pitchForm, type: e.target.value })}
                                        >
                                            <option value="shirt">{t('sponsor_page.type_shirt')}</option>
                                            <option value="board">{t('sponsor_page.type_board')}</option>
                                            <option value="ball">{t('sponsor_page.type_ball')}</option>
                                            <option value="general">{t('sponsor_page.type_general')}</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">{t('sponsor_page.tone_label')}</label>
                                        <div className="flex bg-gray-100 dark:bg-white/5 p-1 rounded-xl">
                                            {[
                                                { id: 'friendly', label: t('sponsor_page.tone_friendly') },
                                                { id: 'formal', label: t('sponsor_page.tone_formal') },
                                                { id: 'passionate', label: t('sponsor_page.tone_passionate') }
                                            ].map(opt => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    onClick={() => setPitchForm({ ...pitchForm, tone: opt.id })}
                                                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${pitchForm.tone === opt.id
                                                        ? 'bg-white dark:bg-[#161224] shadow text-emerald-600 dark:text-emerald-400'
                                                        : 'text-gray-500 hover:text-gray-700 dark:text-slate-400'
                                                        }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            label={t('sponsor_page.club_name_label')}
                                            value={pitchForm.verenigingNaam}
                                            onChange={(e) => setPitchForm({ ...pitchForm, verenigingNaam: e.target.value })}
                                            placeholder={t('sponsor_page.club_name_placeholder')}
                                            className="dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
                                        />
                                        <Input
                                            label={t('sponsor_page.sender_name_label')}
                                            value={pitchForm.contactNaam}
                                            onChange={(e) => setPitchForm({ ...pitchForm, contactNaam: e.target.value })}
                                            placeholder={t('sponsor_page.sender_name_placeholder')}
                                            className="dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
                                        />
                                    </div>

                                    <Button
                                        onClick={generatePitch}
                                        disabled={pitchLoading}
                                        className="w-full bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white shadow-lg py-4 text-lg"
                                    >
                                        {pitchLoading ? <RefreshCw className="animate-spin mr-2" /> : <Sparkles className="mr-2" />}
                                        {pitchLoading ? t('sponsor_page.btn_generating') : t('sponsor_page.btn_generate')}
                                    </Button>
                                </div>
                            </div>

                            {/* Result Section */}
                            <div className="relative">
                                <div className={`bg-white dark:bg-[#130f22] p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-white/5 h-full flex flex-col ${pitchLoading ? 'opacity-50' : ''}`}>
                                    <div className="flex justify-between items-center mb-6">
                                        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                            <Mail className="text-purple-500" />
                                            {t('sponsor_page.result_title')}
                                        </h2>
                                        {pitchResult && (
                                            <Button variant="secondary" size="sm" onClick={copyToClipboard}>
                                                <Copy size={16} className="mr-2" /> {t('sponsor_page.btn_copy')}
                                            </Button>
                                        )}
                                    </div>

                                    <div className="flex-1 bg-gray-50 dark:bg-slate-950/40 rounded-xl p-6 border border-gray-100 dark:border-white/5 overflow-y-auto max-h-[600px]">
                                        {pitchResult ? (
                                            <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-line text-gray-700 dark:text-slate-300 leading-relaxed">
                                                {pitchResult}
                                            </div>
                                        ) : (
                                            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                                <Sparkles size={48} className="mb-4 text-gray-200 dark:text-slate-700" />
                                                <p className="text-center whitespace-pre-line">{t('sponsor_page.result_placeholder')}</p>
                                            </div>
                                        )}
                                    </div>

                                    {pitchResult && (
                                        <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-950/10 text-yellow-800 dark:text-yellow-400 text-xs rounded-xl flex gap-2 items-start border border-yellow-100 dark:border-yellow-500/20">
                                            <span>💡</span>
                                            <p>{t('sponsor_page.tip')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

            {/* Add/Edit Sponsor Modal */}
            <Modal
                isOpen={showSponsorModal}
                onClose={() => {
                    setShowSponsorModal(false)
                    setEditingSponsor(null)
                }}
                title={editingSponsor ? 'Sponsor Bewerken' : 'Nieuwe Sponsor Toevoegen'}
                size="lg"
            >
                <form onSubmit={handleSponsorSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                            label="Sponsor Naam"
                            value={sponsorForm.name}
                            onChange={e => setSponsorForm({ ...sponsorForm, name: e.target.value })}
                            placeholder="Bijv. Rabobank, Coca-Cola"
                            required
                            className="dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
                        />

                        <Input
                            label="Website URL (Optioneel)"
                            value={sponsorForm.website_url}
                            onChange={e => setSponsorForm({ ...sponsorForm, website_url: e.target.value })}
                            placeholder="https://www.sponsor.nl"
                            className="dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
                        />
                    </div>

                    <div>
                        <MultiImageUploader
                            label="Sponsor Logo Uploaden (Optioneel)"
                            value={sponsorForm.logo_url}
                            onChange={(url) => setSponsorForm({ ...sponsorForm, logo_url: url })}
                            allowMultiple={false}
                            previewHeight="h-24"
                        />
                        <p className="text-[10px] text-gray-500 dark:text-slate-400 mt-1">
                            Laat dit leeg om automatisch een strakke, gekleurde gradient-card met de sponsorletters te genereren.
                        </p>
                    </div>

                    {/* Presets Row */}
                    {!editingSponsor && (
                        <div>
                            <label className="text-xs font-bold text-gray-600 dark:text-slate-300 block mb-2">Snelle Demo Presets (Testlogo's)</label>
                            <div className="flex flex-wrap gap-2">
                                {PRESET_SPONSORS.map(preset => (
                                    <button
                                        key={preset.name}
                                        type="button"
                                        onClick={() => applyPreset(preset)}
                                        className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 text-[10px] font-bold text-gray-700 dark:text-slate-300 flex items-center gap-1.5"
                                    >
                                        <span>✨</span>
                                        {preset.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Display duration slider */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm font-bold text-gray-700 dark:text-slate-300">Weergave Looptijd (in seconden)</label>
                            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-2 py-0.5 rounded-lg">{sponsorForm.display_duration}s</span>
                        </div>
                        <input
                            type="range"
                            min="3"
                            max="30"
                            step="1"
                            value={sponsorForm.display_duration}
                            onChange={e => setSponsorForm({ ...sponsorForm, display_duration: parseInt(e.target.value) })}
                            className="w-full h-2 bg-gray-200 dark:bg-white/5 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                        />
                        <div className="flex justify-between text-[10px] text-gray-400 dark:text-slate-500 mt-1">
                            <span>Snelle rotatie (3s)</span>
                            <span>Normaal</span>
                            <span>Lange looptijd (30s)</span>
                        </div>
                    </div>

                    {/* Active Toggle Switch */}
                    <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 dark:border-white/5 bg-gray-50 dark:bg-white/5">
                        <div>
                            <span className="font-bold block text-sm text-gray-800 dark:text-slate-200">Sponsor Activeren</span>
                            <p className="text-[10px] text-gray-500 dark:text-slate-400">Als de sponsor actief is, rooteert deze mee op schermen.</p>
                        </div>
                        <div 
                            className="relative inline-flex items-center cursor-pointer select-none"
                            onClick={() => setSponsorForm(f => ({ ...f, is_active: !f.is_active }))}
                        >
                            <div className={`w-12 h-7 rounded-full p-1 transition-colors duration-300 shadow-inner relative flex items-center ${
                                sponsorForm.is_active 
                                    ? 'bg-emerald-500' 
                                    : 'bg-slate-700/50'
                            }`}>
                                <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform duration-300 ${
                                    sponsorForm.is_active ? 'translate-x-5' : 'translate-x-0'
                                }`} />
                            </div>
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex justify-end gap-3 border-t border-gray-100 dark:border-white/5 pt-4">
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={() => {
                                setShowSponsorModal(false)
                                setEditingSponsor(null)
                            }}
                        >
                            Annuleren
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                        >
                            {editingSponsor ? 'Sponsor Opslaan' : 'Sponsor Toevoegen'}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    )
}
